import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { hasMinimumRole, getLoginPath, getHomePath } from '../../utils/permissions';
import LoadingSpinner from '../LoadingSpinner';
import AccessDenied from './AccessDenied';

/**
 * RequireAuth - Route guard component that enforces authentication and authorization
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components to render if authorized
 * @param {string} [props.tier] - Required tier ('tenant' or 'platform')
 * @param {string} [props.role] - Minimum required role
 * @returns {React.ReactNode}
 */
export const RequireAuth = ({ children, tier, role }) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  // Not authenticated - redirect to appropriate login
  if (!isAuthenticated) {
    const loginPath = getLoginPath(tier);
    return <Navigate to={loginPath} state={{ from: location }} replace />;
  }

  // Check tier requirement
  if (tier && user.tier !== tier) {
    // Wrong tier - redirect to user's home
    const homePath = getHomePath(user);
    return <Navigate to={homePath} replace />;
  }

  // Check role requirement
  if (role && !hasMinimumRole(user.role, role)) {
    return <AccessDenied />;
  }

  return children;
};

export default RequireAuth;
